<script setup lang="ts">
import { ref, type HTMLAttributes } from 'vue'
import { cn } from '@/lib/utils'
import { useVModel } from '@vueuse/core'
import { Bold, Italic, Strikethrough, Code, Smile, EyeOff } from 'lucide-vue-next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import EmojiPicker from 'vue3-emoji-picker'
import 'vue3-emoji-picker/css'

const props = defineProps<{
  class?: HTMLAttributes['class']
  defaultValue?: string | number
  modelValue?: string | number
  placeholder?: string
  rows?: number
  variables?: { label: string, value: string }[]
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
})

const textareaRef = ref<HTMLTextAreaElement | null>(null)
const showEmojiPicker = ref(false)

const applyFormat = (tag: string, endTag?: string) => {
  const textarea = textareaRef.value
  if (!textarea) return

  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const text = String(modelValue.value || '')
  const selectedText = text.substring(start, end)
  
  const actualEndTag = endTag || tag.replace('<', '</')
  const newText = text.substring(0, start) + tag + selectedText + actualEndTag + text.substring(end)
  
  modelValue.value = newText
  
  setTimeout(() => {
    textarea.focus()
    if (selectedText.length === 0) {
      const newPos = start + tag.length
      textarea.setSelectionRange(newPos, newPos)
    } else {
      const newPos = start + tag.length + selectedText.length + actualEndTag.length
      textarea.setSelectionRange(newPos, newPos)
    }
  }, 0)
}

const insertText = (content: string) => {
  const textarea = textareaRef.value
  if (!textarea) return

  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const text = String(modelValue.value || '')
  
  const newText = text.substring(0, start) + content + text.substring(end)
  modelValue.value = newText
  
  setTimeout(() => {
    textarea.focus()
    const newPos = start + content.length
    textarea.setSelectionRange(newPos, newPos)
  }, 0)
}

const onEmojiSelect = (emoji: any) => {
  insertText(emoji.i)
  showEmojiPicker.value = false
}
</script>

<template>
  <div class="flex flex-col gap-2 w-full group relative">
    <div class="flex flex-wrap items-center gap-1 p-1 bg-zinc-900/50 border border-zinc-800 rounded-t-md border-b-0 transition-colors group-focus-within:border-primary/50">
      <TooltipProvider>
        <Tooltip :delay-duration="100">
          <TooltipTrigger as-child>
            <button @click="applyFormat('<b>', '</b>')" type="button" class="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors">
              <Bold class="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Negrito</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip :delay-duration="100">
          <TooltipTrigger as-child>
            <button @click="applyFormat('<i>', '</i>')" type="button" class="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors">
              <Italic class="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Itálico</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip :delay-duration="100">
          <TooltipTrigger as-child>
            <button @click="applyFormat('<s>', '</s>')" type="button" class="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors">
              <Strikethrough class="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Riscado</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip :delay-duration="100">
          <TooltipTrigger as-child>
            <button @click="applyFormat('<code>', '</code>')" type="button" class="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors">
              <Code class="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Monoespaçado</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip :delay-duration="100">
          <TooltipTrigger as-child>
            <button @click="applyFormat('<tg-spoiler>', '</tg-spoiler>')" type="button" class="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors" title="Spoiler">
              <EyeOff class="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Spoiler</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <div class="relative">
        <TooltipProvider>
          <Tooltip :delay-duration="100">
            <TooltipTrigger as-child>
              <button @click="showEmojiPicker = !showEmojiPicker" type="button" :class="cn('p-1.5 rounded hover:bg-white/10 transition-colors', showEmojiPicker ? 'bg-white/10 text-primary' : 'text-zinc-400 hover:text-white')">
                <Smile class="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">Emojis</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div v-if="showEmojiPicker" class="absolute top-full left-0 mt-1 z-50 shadow-xl">
          <EmojiPicker
            :native="true"
            theme="dark"
            @select="onEmojiSelect"
            class="!bg-zinc-900 !border-zinc-800"
          />
        </div>
      </div>

      <div class="h-4 w-px bg-zinc-800 mx-1" v-if="variables?.length" />

      <template v-for="variable in variables" :key="variable.value">
        <TooltipProvider>
          <Tooltip :delay-duration="100">
            <TooltipTrigger as-child>
              <button @click="insertText(variable.value)" type="button" class="px-2 py-1 text-[10px] uppercase font-bold text-zinc-500 hover:text-primary transition-colors hover:bg-primary/5 rounded border border-transparent hover:border-primary/20">
                {{ variable.label }}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">Inserir {{ variable.label }}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </template>
    </div>
    <textarea 
      ref="textareaRef"
      v-model="modelValue" 
      :placeholder="props.placeholder"
      :rows="props.rows || 3"
      :class="cn('flex min-h-20 w-full rounded-b-md border border-zinc-800 bg-white/5 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50 text-white transition-colors', props.class)" 
    />
    <div v-if="showEmojiPicker" class="fixed inset-0 z-40" @click="showEmojiPicker = false" />
  </div>
</template>
